import { NavLink, Navigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Users, MapPin, Swords, Settings, Trophy, LogOut, Crosshair, CalendarRange, Hammer, ScrollText, DatabaseBackup } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ConnectionBanner } from "./ConnectionBanner";

const NAV = [
  { to: "/dashboard", label: "Painel", icon: LayoutDashboard, id: "dashboard" },
  { to: "/orgs", label: "Organizações", icon: Users, id: "orgs" },
  { to: "/zones", label: "Zonas", icon: MapPin, id: "zones" },
  { to: "/wars", label: "Guerras", icon: Swords, id: "wars" },
  { to: "/receitas", label: "Receitas", icon: Hammer, id: "recipes" },
  { to: "/temporadas", label: "Temporadas", icon: CalendarRange, id: "seasons" },
  { to: "/auditoria", label: "Auditoria", icon: ScrollText, id: "audit" },
  { to: "/copias", label: "Cópias de segurança", icon: DatabaseBackup, id: "backups" },
  { to: "/settings", label: "Configurações", icon: Settings, id: "settings" },
];

export const Brand = () => (
  <div className="flex items-center gap-3">
    <div className="grid h-9 w-9 place-items-center border border-emerald-500/40 bg-emerald-500/10">
      <Crosshair className="h-5 w-5 text-emerald-400" />
    </div>
    <div className="leading-tight">
      <div className="font-display text-sm font-bold uppercase tracking-[0.18em] text-slate-100">Domínio</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">de Crafts</div>
    </div>
  </div>
);

const Sidebar = ({ user, logout }) => (
  <aside className="fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-slate-800 bg-slate-950/95">
    <div className="border-b border-slate-800 px-5 py-5"><Brand /></div>
    <nav className="flex-1 space-y-1 px-3 py-6">
      <div className="eyebrow mb-3 px-2 text-slate-500">Operações</div>
      {NAV.map(({ to, label, icon: Icon, id }) => (
        <NavLink key={to} to={to} data-testid={`nav-${id}-link`}
          className={({ isActive }) => `nav-link flex items-center gap-3 border-l-2 px-3 py-2.5 text-sm font-medium ${
            isActive ? "border-emerald-400 bg-emerald-500/10 text-emerald-300" : "border-transparent text-slate-400 hover:bg-slate-900 hover:text-slate-100"}`}>
          <Icon className="h-4 w-4" />{label}
        </NavLink>
      ))}
      <a href="/ranking" target="_blank" rel="noreferrer" data-testid="nav-public-ranking-link"
        className="nav-link mt-6 flex items-center gap-3 border-l-2 border-transparent px-3 py-2.5 text-sm text-slate-400 hover:bg-slate-900 hover:text-amber-300">
        <Trophy className="h-4 w-4" />Ranking público
      </a>
    </nav>
    <div className="border-t border-slate-800 p-4">
      <div className="truncate font-mono text-xs text-slate-500" data-testid="current-user-email">{user.email}</div>
      <button onClick={logout} data-testid="logout-button"
        className="nav-link mt-3 flex w-full items-center justify-center gap-2 border border-slate-800 py-2 text-xs uppercase tracking-widest text-slate-400 hover:border-rose-500/50 hover:text-rose-400">
        <LogOut className="h-3.5 w-3.5" />Sair
      </button>
    </div>
  </aside>
);

export const ProtectedLayout = () => {
  const { user, logout } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center font-mono text-sm text-slate-500" data-testid="auth-loading">A verificar sessão…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen grid-bg">
      <Sidebar user={user} logout={logout} />
      <ConnectionBanner />
      <main className="ml-60 min-h-screen px-8 py-8 lg:px-12"><Outlet /></main>
    </div>
  );
};

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="fade-up mb-8 flex flex-wrap items-end justify-between gap-4">
    <div>
      <div className="eyebrow">{eyebrow}</div>
      <h1 className="mt-2 text-3xl font-bold uppercase tracking-wider text-slate-50 sm:text-4xl" data-testid="page-title">{title}</h1>
    </div>
    <div className="flex items-center gap-3">{children}</div>
  </div>
);
